import { useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useUpdateDocument } from '../lib/queries.js';
import type { Document as DocDTO } from '../lib/queries.js';
import { DocumentEditor } from '../editor/Editor.js';
import { useStore } from '../store/useStore.js';
import { Upload, Loader2 } from 'lucide-react';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function DocumentEditorPage() {
  const { slug, documentId } = useParams({ strict: false }) as {
    slug: string;
    documentId: string;
  };
  const qc = useQueryClient();
  const docKey = ['document', documentId] as const;

  // Carrega documento sob demanda.
  const cached = qc.getQueryData<DocDTO>(docKey);
  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    api<DocDTO>(`/documents/${documentId}`)
      .then((d) => {
        if (!cancelled) qc.setQueryData(docKey, d);
      })
      .catch(() => {
        /* TODO toast */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const update = useUpdateDocument(undefined);
  const workspaceId = useStore((s) => s.workspaceId);
  const nodes = useStore((s) => s.nodes);
  const nd = nodes.find((n) => n.id === documentId);
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !workspaceId || !cached) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('workspaceId', workspaceId);
      const res = await fetch(`/api/assets/upload-direct?workspaceId=${workspaceId}`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      
      update.mutate({ id: documentId, patch: { content: data.assetId } });
      qc.setQueryData(docKey, { ...cached, content: data.assetId });
    } catch (err) {
      console.error(err);
      setUploadError('Erro ao enviar a imagem. Tente novamente.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // Debounce de auto-save 800ms.
  const timeoutRef = useRef<number | null>(null);
  const onChange = useMemo(
    () => (next: any) => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        update.mutate({ id: documentId, patch: { content: next } });
      }, 800);
    },
    [documentId, update],
  );

  const docType = nd?.type || cached?.type || 'idea';
  const docContent = nd?.content !== undefined && nd.content !== null ? nd.content : (cached?.content || null);

  const [localTitle, setLocalTitle] = useState(cached?.title || '');
  useEffect(() => {
    if (cached) setLocalTitle(cached.title);
  }, [cached]);

  // Debounce de auto-save title
  const titleTimeoutRef = useRef<number | null>(null);
  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalTitle(val);
    if (titleTimeoutRef.current) window.clearTimeout(titleTimeoutRef.current);
    titleTimeoutRef.current = window.setTimeout(() => {
      update.mutate({ id: documentId, patch: { title: val } });
    }, 800);
  };

  if (!cached) return <div className="absolute inset-0 grid place-items-center text-zinc-500">carregando documento…</div>;

  return (
    <aside className="absolute right-0 top-0 h-full w-[540px] border-l border-zinc-800 bg-zinc-950/95 backdrop-blur p-8 overflow-y-auto shadow-2xl">
      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-6 font-mono bg-zinc-900/50 w-fit px-2 py-1 rounded">
        <span className="material-symbols-outlined text-[14px]">folder</span>
        /{slug}
      </div>
      
      <input 
        className="text-4xl font-bold mb-8 bg-transparent outline-none border-none w-full text-zinc-100 placeholder-zinc-800 focus:placeholder-zinc-700 transition-colors"
        value={localTitle}
        onChange={onTitleChange}
        placeholder="Título do Documento"
      />
      
      {docType === 'image' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {isUploading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 10, background: 'rgba(255,255,255,0.01)', borderRadius: 8, border: '1px dashed rgba(255,204,0,0.2)' }}>
              <Loader2 className="animate-spin" size={32} style={{ color: '#ffcc00' }} />
              <span style={{ fontSize: 14, color: 'var(--muted)' }}>Enviando imagem...</span>
            </div>
          ) : docContent && typeof docContent === 'string' && (docContent as any).trim() ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
                <img
                  src={UUID_REGEX.test((docContent as any).trim()) ? `/api/assets/${(docContent as any).trim()}/serve` : (docContent as any).trim()}
                  alt={cached.title}
                  style={{ maxWidth: '100%', maxHeight: 380, objectFit: 'contain', borderRadius: 6 }}
                />
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 204, 0, 0.25)',
                  borderRadius: 6,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  transition: 'all 0.2s',
                }}
              >
                <Upload size={14} /> Substituir Imagem
              </button>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '1px dashed rgba(255, 204, 0, 0.35)',
                borderRadius: 8,
                padding: '48px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.01)',
                transition: 'all 0.2s',
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <Upload size={24} style={{ color: '#ffcc00' }} />
              <span style={{ fontSize: 14, fontWeight: 500, color: 'white' }}>Clique para enviar imagem</span>
              <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>Formatos suportados: PNG, JPG, WEBP</span>
            </div>
          )}
          {uploadError && (
            <div style={{ fontSize: 12, color: 'var(--error, #ef4444)', marginTop: 8, textAlign: 'center' }}>
              {uploadError}
            </div>
          )}
        </div>
      ) : (
        <DocumentEditor documentId={documentId} initial={docContent} onChange={onChange} />
      )}
    </aside>
  );
}
